/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import Imgix from 'js/components/Imgix';
import connectToStores from 'js/lib/connectToStores';
import type UserAgentInfo from 'js/lib/useragent';

import { Grid, Link, Typography, Typography2 } from '@coursera/cds-core';
import { SvgLevelAdvanced, SvgLevelBeginner, SvgLevelIntermediate } from '@coursera/coursera-ui/svg';

import { GENERIC_HEADER } from 'bundles/browse/constants';
import Ratings from 'bundles/collections/components/Ratings';
import type { CollectionItemsFragment_courses_elements_courseTypeMetadata as CourseTypeMetadata } from 'bundles/collections/components/__generated__/CollectionItemsFragment';
import type {
  CollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements_courses_elements as CoursesV1,
  CollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements_degrees_elements as DegreesV1,
  CollectionPageQuery_CatalogCollectionPagesV1Resource_bySlug_elements_s12ns_elements as OnDemandSpecializationsV1,
} from 'bundles/collections/components/__generated__/CollectionPageQuery';
import type { org_coursera_ondemand_common_DifficultyLevel as DifficultyLevelType } from 'bundles/collections/components/__generated__/globalTypes';
import {
  COURSE_LABEL,
  DEGREE_LABEL,
  GRADUATE_CERTIFICATE_LABEL,
  GUIDED_PROJECTS_LABEL,
  MASTERTRACK_LABEL,
  PRODUCT_TYPE_TO_PATHS,
  PROJECTS_LABEL,
  UNIVERSITY_CERTIFICATE_LABEL,
} from 'bundles/collections/constants';
import type { ProductType } from 'bundles/collections/types';
import ProductTypePill from 'bundles/growth-ui/components/ProductTypePill';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import PartnersNames from 'bundles/partners-graphql/components/PartnerNames';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';
import { getCollectionItemS12nTypeName, getSdpUrl } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import 'css!bundles/collections/components/__styles__/CollectionItem';

type TrackingData = {
  entity_index: number;

  collection_title: string;
  slug: string;
  name: string;
  s12nId?: string;
  courseId?: string;
};

type CoursesAndS12ns = CoursesV1 | OnDemandSpecializationsV1 | null;

type ItemWithDifficulty = CoursesAndS12ns & {
  level: DifficultyLevelType;
  productVariant: string;
  courseTypeMetadata: CourseTypeMetadata | null;
};

type InputProps = {
  item: ItemWithDifficulty | DegreesV1;
  data: TrackingData;
};

type Props = InputProps & {
  userAgent: UserAgentInfo;
};

const DIFFICULTY_LEVEL_TO_ICON = {
  BEGINNER: SvgLevelBeginner,
  INTERMEDIATE: SvgLevelIntermediate,
  ADVANCED: SvgLevelAdvanced,
};

const styles = {
  collectionItem: css`
    display: block;
    position: relative;
    width: 100%;
    border-radius: 3px;
    overflow: hidden;
    border-bottom: 1px solid #efefef;

    &:first-child {
      border-top: 1px solid #efefef;
    }
  `,
  imageWrapper: css`
    position: relative;
    height: 100%;
    width: 150px;
    margin-right: 20px;
    float: left;
    overflow: hidden;

    @media (max-width: 750px) {
      float: right;
      height: 80px;
      width: 80px;
      margin-right: 0;
      margin-left: 20px;
    }
  `,
  productPhoto: css`
    max-width: 240px;
    max-height: 130px;
  `,
  itemMetadataString: css`
    text-transform: uppercase;
  `,
  itemName: css`
    -webkit-line-clamp: 2;
    margin-bottom: 10px;
  `,
  partnerNames: css`
    @media (max-width: 750px) {
      order: -1;
    }
  `,

  difficultyLevel: css`
    margin-top: 1rem;
  `,
};

function getDifficultyLevelString(level: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED') {
  const DIFFICULTY_LEVEL_TO_STRING = {
    BEGINNER: 'Beginner Level',
    INTERMEDIATE: 'Intermediate Level',
    ADVANCED: 'Advanced Level',
  };
  return DIFFICULTY_LEVEL_TO_STRING[level];
}

class CollectionItem extends React.Component<Props> {
  getProductTypeName = (
    isGuidedProject: boolean,
    isProject: boolean,
    isDegree: boolean,
    isMasterTrack: boolean,
    isUniversityCertificate: boolean,
    s12nProductVariant?: string,
    isGraduateCertificate?: boolean
  ) => {
    if (isGraduateCertificate) {
      return GRADUATE_CERTIFICATE_LABEL;
    }
    if (isUniversityCertificate) {
      return UNIVERSITY_CERTIFICATE_LABEL;
    }
    // Ordering is Important because MasterTracks count as Degrees
    if (s12nProductVariant) {
      return getCollectionItemS12nTypeName(s12nProductVariant);
    } else if (isMasterTrack) {
      return MASTERTRACK_LABEL;
    } else if (isDegree) {
      return DEGREE_LABEL;
    } else if (isGuidedProject) {
      return GUIDED_PROJECTS_LABEL;
    } else if (isProject) {
      return PROJECTS_LABEL;
    } else {
      return COURSE_LABEL;
    }
  };

  constructProductUrl = (slug: string, productTypeName: ProductType) => {
    if (this.props.item.__typename === 'OnDemandSpecializationsV1') {
      return getSdpUrl(this.props.item.productVariant as S12nProductVariant, slug);
    } else {
      return `${PRODUCT_TYPE_TO_PATHS[productTypeName]}/${slug}`;
    }
  };

  renderProductMetaData = () => {
    const { item } = this.props;

    const derivative = (item as ItemWithDifficulty)?.derivative;
    const level = (item as ItemWithDifficulty)?.level;

    const DifficultyIcon = DIFFICULTY_LEVEL_TO_ICON[level];
    const hasNoRatings = derivative?.averageFiveStarRating && derivative?.ratingCount;
    return (
      <Grid container direction="column" spacing={4}>
        {derivative && derivative.averageFiveStarRating && derivative.ratingCount && (
          <Typography2 component="span" variant="bodyPrimary">
            <Ratings averageFiveStarRating={derivative.averageFiveStarRating} ratingCount={derivative.ratingCount} />
          </Typography2>
        )}
        {level && (
          <div className="horizontal-box" css={[!hasNoRatings && styles.difficultyLevel]}>
            <DifficultyIcon size={20} />
            <Typography2 component="span" variant="subtitleMedium" css={styles.itemMetadataString} className="m-l-1">
              {getDifficultyLevelString(level)}
            </Typography2>
          </div>
        )}
      </Grid>
    );
  };

  render() {
    const { userAgent, item, data } = this.props;

    if (item === null || item === undefined) {
      return null;
    }

    const { slug, name, partners, productVariant } = item;
    const photoUrl = item.__typename === 'CoursesV1' ? item.photoUrl : undefined;
    const logo =
      item.__typename === 'OnDemandSpecializationsV1' || item.__typename === 'DegreesV1' ? item.logo : undefined;
    let isGuidedProject = false;
    let isProject = false;
    if (item.__typename === 'CoursesV1' && item.courseTypeMetadata) {
      isGuidedProject =
        item.courseTypeMetadata.courseTypeMetadata.__typename === 'CourseTypeMetadataV1_rhymeProjectMember';
      isProject = item.courseTypeMetadata.courseTypeMetadata.__typename === 'CourseTypeMetadataV1_projectMember';
    }
    const s12nProductVariant = item.__typename === 'OnDemandSpecializationsV1' ? item.productVariant : undefined;
    const isDegree = item.__typename === 'DegreesV1';
    const isMasterTrack = productVariant === 'MasterTrack';
    const isUniversityCertificate = productVariant === 'UniversityCertificate';
    const isGraduateCertificate = productVariant === 'GraduateCertificate';
    const firstPartnerImage = item.partners?.elements?.[0]?.squareLogo;
    const productTypeName = this.getProductTypeName(
      isGuidedProject,
      isProject,
      isDegree,
      isMasterTrack,
      isUniversityCertificate,
      s12nProductVariant,
      isGraduateCertificate
    );

    let logoToUse = '';

    if (isMasterTrack || isDegree) {
      logoToUse = firstPartnerImage || GENERIC_HEADER;
    } else if (s12nProductVariant) {
      logoToUse = logo || GENERIC_HEADER;
    } else {
      logoToUse = photoUrl || GENERIC_HEADER;
    }

    return (
      <Link
        className="nostyle p-y-2"
        css={styles.collectionItem}
        component={TrackedLink2}
        variant="quiet"
        enableVisitedState={false}
        data-e2e="collection-card"
        trackingName="product_card"
        data={data}
        withVisibilityTracking={true}
        href={this.constructProductUrl(slug, productTypeName)}
      >
        <div css={styles.imageWrapper}>
          <Media query={{ maxWidth: 750 }} defaultMatches={userAgent && userAgent.isMobileBrowser}>
            {(matches) =>
              matches ? (
                <Imgix
                  src={logoToUse}
                  alt={name}
                  maxWidth={80}
                  maxHeight={80}
                  imgParams={{ fit: 'crop', auto: 'format,compress' }}
                  css={styles.productPhoto}
                />
              ) : (
                <div>
                  <Imgix
                    src={logoToUse}
                    alt={name}
                    maxWidth={150}
                    maxHeight={150}
                    imgParams={{ fit: 'crop', auto: 'format,compress' }}
                    css={styles.productPhoto}
                  />
                </div>
              )
            }
          </Media>
        </div>
        <div className="horizontal-box">
          <div className="vertical-box">
            <Typography variant="h2semibold" component="h4" css={styles.itemName}>
              {name}
            </Typography>
            <Typography2 css={styles.partnerNames} variant="bodySecondary" component="p" className="m-b-1s">
              {partners && <PartnersNames partners={partners} />}
            </Typography2>
            <ProductTypePill productTypeName={productTypeName} />
            {this.renderProductMetaData()}
          </div>
        </div>
      </Link>
    );
  }
}

export default connectToStores<Props, InputProps, ApplicationStoreClass>(
  [ApplicationStoreClass],
  (ApplicationStore, props) => ({
    ...props,
    userAgent: ApplicationStore.getUserAgent(),
  })
)(CollectionItem);
